import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Modal, Button, Form, InputGroup, FormControl } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { baseurladdress } from "../baseurl";
import * as XLSX from "xlsx";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import "../user/user.css";
import LocationPickerModal from "./LocationPickermodal"; // adjust path to wherever you place the file

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const initialDoctor = {
  doctor_name: "",
  doctor_qualification: "",
  doctor_mobile: "",
  doctor_email: "",
  doctor_gender: "",
  doctor_address: "",
  doctor_districtname: "",
  doctor_statename: "",
  doctor_countryname: "",
  doctor_pin: "",
  doctor_locations: [],
  doctor_available_days: [],
  doctor_servedby: "",
  doctor_status: "1",
};

function Doctor() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("id");
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialDoctor);
  const [loading, setLoading] = useState(false);

  // ── Doctor type tab: "0" = Core Doctors, "1" = Secondary Doctors ──────────
  const [doctorType, setDoctorType] = useState("0");

  // ── Map location picker state ─────────────────────────────────────────────
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [editingLocationIndex, setEditingLocationIndex] = useState(null); // null = adding new

  // ── Sync state ──────────────────────────────────────────────────────────────
  const [syncing, setSyncing] = useState(false);

  // ── Bulk upload states ────────────────────
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileRef = useRef(null);

  // TanStack Table states
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);

  /* ---------------- API ---------------- */

  const api = axios.create({
    baseURL: baseurladdress,
    headers: { Authorization: `Bearer ${token}` },
  });

  const loadDoctors = async () => {
    try {
      setLoading(true);
      const res = await api.get(`doctors/findalldoctors?doctor_type=${doctorType}`);
      setDoctors(res.data.doctors || []);
    } catch {
      Swal.fire("Error", "Failed to load doctors", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorType]);

  // Switching tabs closes any open modal and resets the form/edit state
  const switchDoctorType = (type) => {
    if (type === doctorType) return;
    setDoctorType(type);
    setShowModal(false);
    setEditingId(null);
    setForm(initialDoctor);
  };

  /* ---------------- Handlers ---------------- */

  const openAdd = () => {
    setForm(initialDoctor);
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = async (doctor) => {
    await changeTitle(String(doctor.doctor_country_id));
    await changeDistricts(String(doctor.doctor_state_id));

    setForm({
      doctor_name: doctor.doctor_name ?? "",
      doctor_qualification: doctor.doctor_qualification ?? "",
      doctor_mobile: doctor.doctor_mobile ?? "",
      doctor_email: doctor.doctor_email ?? "",
      doctor_gender: String(doctor.doctor_gender ?? ""),
      doctor_address: doctor.doctor_address ?? "",
      doctor_countryname: String(doctor.doctor_country_id ?? ""),
      doctor_statename: String(doctor.doctor_state_id ?? ""),
      doctor_districtname: String(doctor.doctor_district_id ?? ""),
      doctor_pin: doctor.doctor_pin ?? "",
      doctor_locations: normalizeLocations(doctor),
      doctor_available_days: normalizeAvailableDays(doctor),
      doctor_servedby: String(doctor.doctor_servedby ?? ""),
      doctor_status: String(doctor.doctor_status ?? ""),
    });

    setEditingId(doctor.doctor_id);
    setShowModal(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const normalizeAvailableDays = (doctor) => {
    const rawDays =
      doctor?.doctor_available_days ??
      doctor?.available_days ??
      doctor?.doctor_available_day ??
      doctor?.available_day ??
      "";

    if (Array.isArray(rawDays)) {
      return rawDays.map((day) => String(day).trim()).filter(Boolean);
    }

    if (typeof rawDays === "string") {
      return rawDays
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean);
    }

    return [];
  };

  const toggleAvailableDay = (day) => {
    setForm((prev) => {
      const hasDay = prev.doctor_available_days.includes(day);
      return {
        ...prev,
        doctor_available_days: hasDay
          ? prev.doctor_available_days.filter((item) => item !== day)
          : [...prev.doctor_available_days, day],
      };
    });
  };

  /* ─────────────────── Multiple locations (name + lat/lng) ─────────────────── */

  // Accepts a doctor row from the API. Prefers doctor.doctor_locations (array
  // from loccoordinate_tbl, e.g. [{ loc_name, loc_lat, loc_lng }, ...]) and
  // falls back to the legacy single "lat,lng" string on doctor_coordinates
  // so old records still show up in the edit form.
  const normalizeLocations = (doctor) => {
    if (Array.isArray(doctor.doctor_locations) && doctor.doctor_locations.length > 0) {
      const mapped = doctor.doctor_locations.map((loc) => ({
        loc_address: loc.loc_address ?? "",
        loc_lat: loc.loc_lat ?? "",
        loc_lng: loc.loc_lng ?? "",
        loc_type: loc.loc_type ?? "",
        loc_default: !!Number(loc.loc_default),
      }));
      // Safety net: if nothing came back flagged default (older rows), default the first one.
      if (!mapped.some((l) => l.loc_default) && mapped.length > 0) {
        mapped[0].loc_default = true;
      }
      return mapped;
    }
    if (doctor.doctor_coordinates) {
      const [lat = "", lng = ""] = String(doctor.doctor_coordinates).split(",").map((s) => s.trim());
      return [{ loc_address: doctor.doctor_address || "", loc_lat: lat, loc_lng: lng, loc_type: "", loc_default: true }];
    }
    return [];
  };

  // Opens the map modal. Pass an index to edit that existing location,
  // or leave it null to add a new one.
  const openLocationPicker = (index = null) => {
    setEditingLocationIndex(index);
    setShowLocationPicker(true);
  };

  // Called by LocationPickerModal with { loc_address, loc_lat, loc_lng }
  const handleLocationConfirm = (loc) => {
    setForm((prev) => {
      const locations = [...prev.doctor_locations];
      if (editingLocationIndex !== null) {
        // preserve the existing loc_type/loc_default unless the picker itself returned one
        locations[editingLocationIndex] = {
          ...locations[editingLocationIndex],
          ...loc,
          loc_type: loc.loc_type ?? locations[editingLocationIndex].loc_type ?? "",
          loc_default: locations[editingLocationIndex].loc_default ?? false,
        };
      } else {
        // The very first location added is automatically the default one.
        locations.push({ loc_type: "", loc_default: locations.length === 0, ...loc });
      }
      return { ...prev, doctor_locations: locations };
    });
    setShowLocationPicker(false);
    setEditingLocationIndex(null);
  };

  // Updates just the loc_type (Home / Clinic / Hospital / Other) for a row
  const updateLocationType = (index, loc_type) => {
    setForm((prev) => {
      const locations = [...prev.doctor_locations];
      locations[index] = { ...locations[index], loc_type };
      return { ...prev, doctor_locations: locations };
    });
  };

  const removeLocationRow = (index) => {
    setForm((prev) => {
      const wasDefault = prev.doctor_locations[index]?.loc_default;
      const remaining = prev.doctor_locations.filter((_, i) => i !== index);
      // If the removed row was the default, hand the flag to the new first row.
      if (wasDefault && remaining.length > 0 && !remaining.some((l) => l.loc_default)) {
        remaining[0] = { ...remaining[0], loc_default: true };
      }
      return { ...prev, doctor_locations: remaining };
    });
  };

  // Marks a single location as the default one (only one location can be
  // default at a time), unflagging every other location.
  const setDefaultLocation = (index) => {
    setForm((prev) => ({
      ...prev,
      doctor_locations: prev.doctor_locations.map((loc, i) => ({
        ...loc,
        loc_default: i === index,
      })),
    }));
  };

  const handleSubmit = async () => {
    if (!form.doctor_name) {
      Swal.fire("Warning", "Name is required", "warning");
      return;
    }

    const cleanedLocations = form.doctor_locations
      .map((loc) => ({
        loc_address: loc.loc_address?.trim() || "",
        loc_lat: loc.loc_lat?.toString().trim() || "",
        loc_lng: loc.loc_lng?.toString().trim() || "",
        loc_type: loc.loc_type?.trim() || "",
        loc_default: !!loc.loc_default,
      }))
      .filter((loc) => loc.loc_lat !== "" && loc.loc_lng !== "");

    // Guarantee exactly one default location goes to the server (falls back
    // to the first row if the user never explicitly picked one).
    if (cleanedLocations.length > 0 && !cleanedLocations.some((l) => l.loc_default)) {
      cleanedLocations[0].loc_default = true;
    }

    try {
      const payload = {
        ...form,
        doctor_locations: cleanedLocations,
        doctor_available_days: Array.isArray(form.doctor_available_days)
          ? form.doctor_available_days
          : [],
        doctor_id: editingId,
        doctor_type: doctorType, // 0 = Core Doctor, 1 = Secondary Doctor
      };

      if (!editingId) {
        payload.doctor_crtby = userId;
        payload.doctor_introby = userId;
      } else {
        payload.doctor_updby = userId;
      }

      const url = editingId ? "doctors/updatedoctor" : "doctors/adddoctor";
      await api.post(url, payload);

      Swal.fire("Success", "Doctor saved successfully", "success");
      setShowModal(false);
      loadDoctors();
    } catch {
      Swal.fire("Error", "Failed to save doctor", "error");
    }
  };

  const deleteDoctor = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.post(`doctors/deletedoctor/${id}`);
      Swal.fire("Deleted", "Doctor removed", "success");
      loadDoctors();
    } catch {
      Swal.fire("Error", "Deletion failed", "error");
    }
  };

  /* ─────────────────── Sync Handler ─────────────────── */

  const handleSync = async () => {
    const confirm = await Swal.fire({
      title: "Sync Doctors?",
      text: "This will fetch doctors from the live database and sync them here.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Sync",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setSyncing(true);
    try {
      // Calls YOUR Node backend — the external API call happens server-side (hidden from browser)
      const res = await api.post("doctors/syncdoctors", {
        startindex: 0,
        endindex: 100, // keep at 10 for initial validation; increase after confirming
      });

      const { synced = 0, failed = 0, errors = [] } = res.data;

      let msg = `${synced} doctor(s) synced successfully.`;
      if (failed > 0) msg += ` ${failed} failed.`;

      Swal.fire({
        title: "Sync Complete",
        html: `
          <div style="text-align:left; font-size:14px;">
            <p>✅ Synced: <strong>${synced}</strong></p>
            <p>❌ Failed: <strong>${failed}</strong></p>
            ${errors.length > 0
              ? `<details><summary style="cursor:pointer; color:#dc3545;">View errors</summary>
                  <ul style="margin-top:6px; padding-left:16px;">
                    ${errors.map(e => `<li>Index ${e.index}: ${e.reason}</li>`).join("")}
                  </ul>
                </details>`
              : ""}
          </div>`,
        icon: synced > 0 ? "success" : "warning",
      });

      loadDoctors();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Sync failed";
      Swal.fire("Sync Error", errMsg, "error");
    } finally {
      setSyncing(false);
    }
  };

  /* ─────────────────── Bulk Upload Handlers (kept, UI commented out) ─────────────────── */

  /** Parse uploaded Excel and validate rows */
  const handleBulkFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const allArrays = XLSX.utils.sheet_to_json(ws, { defval: "", header: 1 });
        const headerIdx = allArrays.findIndex(r => r.some(c => String(c).trim() !== ""));
        const headers   = allArrays[headerIdx].map(h => String(h).trim());
        const rawRows = allArrays.slice(headerIdx + 1)
          .map(arr => Object.fromEntries(headers.map((h, i) => [h, arr[i] ?? ""])))
          .filter(row => Object.values(row).some(v => String(v).trim() !== ""));
  
        const get = (row, ...keys) => {
          for (const key of keys) {
            if (row[key] !== undefined && String(row[key]).trim() !== "") return row[key];
            const found = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
            if (found && String(row[found]).trim() !== "") return row[found];
          }
          return "";
        };
  
        const errors = [];
        const parsed = rawRows.map((row, idx) => {
          const rowNum = idx + 2;
          const doctor_name        = String(get(row, "DOCTORS NAME", "Doctors Name", "doctor_name", "Doctor Name") || "").trim();
          const doctor_mobile      = String(get(row, "MOBILE NO", "Mobile No", "Mobile", "doctor_mobile") || "").trim();
          const doctor_degree      = String(get(row, "DEGREE", "Degree", "doctor_degree") || "").trim();
          const doctor_specialization = String(get(row, "SPECIALIZATION", "Specialization", "doctor_specialization") || "").trim();
          const doctor_hospcli     = String(get(row, "CLINIC/HOSPITAL NAME", "Clinic/Hospital Name", "Clinic", "Hospital", "doctor_hospcli") || "").trim();
          const doctor_address     = String(get(row, "PLACE NAME", "Place Name", "Place", "doctor_address", "Address") || "").trim();
          const doctor_coordinates = String(get(row, "LOCATION", "Location", "doctor_coordinates", "Coordinates") || "").trim();
          const doctor_qualification = String(get(row, "QUALIFICATION", "Qualification", "doctor_qualification") || "").trim();
          const doctor_email       = String(get(row, "EMAIL", "Email", "doctor_email") || "").trim();
          const doctor_pin         = String(get(row, "PIN", "Pin", "doctor_pin") || "").trim();
          const doctor_countryname = String(get(row, "COUNTRY", "Country", "doctor_countryname") || "").trim();
          const doctor_statename   = String(get(row, "STATE", "State", "doctor_statename") || "").trim();
          const doctor_districtname = String(get(row, "DISTRICT", "District", "doctor_districtname") || "").trim();
          const doctor_branch      = String(get(row, "BRANCH", "Branch", "doctor_branch") || "").trim();
          const rawGender          = get(row, "GENDER", "Gender", "doctor_gender")?.toString().trim().toLowerCase();
          const doctor_gender      = rawGender === "male" ? "1" : rawGender === "female" ? "2" : String(rawGender || "");
  
          if (!doctor_name) errors.push(`Row ${rowNum}: Doctor name is required`);
  
          return {
            doctor_name, doctor_qualification, doctor_mobile, doctor_email,
            doctor_gender, doctor_address, doctor_countryname, doctor_statename,
            doctor_districtname, doctor_pin, doctor_coordinates, doctor_degree,
            doctor_specialization, doctor_hospcli, doctor_branch,
            doctor_status: "1", doctor_servedby: null,
            doctor_type: doctorType, // 0 = Core Doctor, 1 = Secondary Doctor
          };
        });
  
        setBulkErrors(errors);
        setBulkRows(parsed);
      } catch {
        Swal.fire("Error", "Failed to read Excel file. Please use the correct template.", "error");
      }
    };
    reader.readAsBinaryString(file);
  };

  /** Submit all parsed rows to the backend bulk endpoint */
  const handleBulkSubmit = async () => {
    if (bulkErrors.length > 0) {
      Swal.fire("Fix Errors", "Please fix the highlighted errors before uploading.", "warning");
      return;
    }
    if (bulkRows.length === 0) {
      Swal.fire("Empty", "No rows to upload.", "warning");
      return;
    }
    setBulkUploading(true);
    try {
      const res = await api.post("doctors/bulkadddoctors", { doctors: bulkRows, userid: userId });
      const { inserted = 0, errors = [] } = res.data;
      let msg = `${inserted} doctor(s) added successfully.`;
      if (errors.length > 0) msg += ` ${errors.length} row(s) failed.`;
      Swal.fire("Done", msg, inserted > 0 ? "success" : "warning");
      setShowBulkModal(false);
      setBulkRows([]);
      setBulkErrors([]);
      if (bulkFileRef.current) bulkFileRef.current.value = "";
      loadDoctors();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.error || "Bulk upload failed", "error");
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkRows([]);
    setBulkErrors([]);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };

  /* ─────────────────── Location dropdowns ─────────────────── */

  const [cntry_name, setcntry_name] = useState([]);
  useEffect(() => {
    const getCountry = async () => {
      try {
        const res = await axios.get(baseurladdress + "staff/getallcountry", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status === "Success") setcntry_name(res.data.countries);
        else setcntry_name([]);
      } catch (err) {
        console.error(err);
        setcntry_name([]);
      }
    };
    getCountry();
  }, [token]);

  const [state_name, setstate_name] = useState([]);
  const changeTitle = async (countryId) => {
    setForm({ ...form, doctor_countryname: countryId, doctor_statename: "", doctor_districtname: "" });
    try {
      const res = await axios.get(baseurladdress + `staff/getstates/${countryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setstate_name(res.data.states || []);
      setdistrict_name([]);
    } catch (err) {
      console.error(err);
      setstate_name([]);
    }
  };

  const [district_name, setdistrict_name] = useState([]);
  const changeDistricts = async (stateId) => {
    setForm({ ...form, doctor_statename: stateId, doctor_districtname: "" });
    try {
      const res = await axios.get(baseurladdress + `staff/getdistricts/${stateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setdistrict_name(res.data.districts || []);
    } catch (err) {
      console.error(err);
      setdistrict_name([]);
    }
  };

  const [staffList, setStaffList] = useState([]);
  useEffect(() => {
    const getAllStaff = async () => {
      try {
        const res = await axios.get(baseurladdress + "staff/findallstaff", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status === "Success") setStaffList(res.data.staff || []);
        else setStaffList([]);
      } catch (err) {
        console.error(err);
        setStaffList([]);
      }
    };
    getAllStaff();
  }, [token]);

  /* ─────────────────── TanStack Table ─────────────────── */

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "Sl.No",
        size: 60,
        cell: ({ row }) => row.index + 1,
      },
      { accessorKey: "doctor_name", header: "Name", size: 150 },
      { accessorKey: "doctor_qualification", header: "Qualification", size: 150 },
      // {
      //   accessorKey: "doctor_degree",
      //   header: "Degree",
      //   size: 130,
      //   cell: ({ row }) => row.original.doctor_degree || "-",
      // },
      // {
      //   accessorKey: "doctor_specialization",
      //   header: "Specialization",
      //   size: 150,
      //   cell: ({ row }) => row.original.doctor_specialization || "-",
      // },
      { accessorKey: "doctor_mobile", header: "Mobile", size: 120 },
      { accessorKey: "doctor_address", header: "Address", size: 200 },
      { accessorKey: "doctor_districtname", header: "District", size: 120 },
      {
        accessorKey: "staff_name",
        header: "Served By",
        size: 150,
        cell: ({ row }) => row.original.staff_name || "-",
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Icon.PencilSquare
              className="text-primary"
              style={{ cursor: "pointer" }}
              onClick={() => openEdit(row.original)}
            />
            <Icon.Trash
              className="text-danger"
              style={{ cursor: "pointer" }}
              onClick={() => deleteDoctor(row.original.doctor_id)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: doctors,
    columns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  /* ─────────────────── UI ─────────────────── */

  return (
    <div className="content-wrap">
      <div className="main">
        <div className="container-fluid">
          <section id="main-content">

            <div className="card">
              {/* ── Card Header ── */}
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0">
                  {doctorType === "1" ? "Secondary Doctors" : "Core Doctors"}
                </h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => openAdd()}
                  >
                    <Icon.PersonPlusFill size={15} className="me-1" />
                    Add Doctor
                  </Button>

                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setShowBulkModal(true)}
                  >
                    <Icon.CloudUpload size={15} className="me-1" />
                    Bulk Import
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => { handleSync(); e.currentTarget.blur(); }}
                    disabled={true}
                    title="Sync is temporarily disabled"
                  >
                    <Icon.ArrowRepeat size={15} className="me-1" />
                    Sync
                  </Button>
                </div>
              </div>

              {/* ── Doctor Type Tabs ── */}
              <div className="px-3 pt-3 d-flex gap-2 border-bottom">
                <button
                  type="button"
                  className={`btn btn-sm ${doctorType === "0" ? "btn-primary" : "btn-outline-primary"}`}
                  style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                  onClick={() => switchDoctorType("0")}
                >
                  Core Doctors
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${doctorType === "1" ? "btn-primary" : "btn-outline-primary"}`}
                  style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                  onClick={() => switchDoctorType("1")}
                >
                  Secondary Doctors
                </button>
              </div>

              <div className="card-body">
                {/* Search and Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Show</span>
                    <Form.Select
                      size="sm"
                      style={{ width: "70px" }}
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                      {[10, 25, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>{pageSize}</option>
                      ))}
                    </Form.Select>
                    <span className="text-muted small">entries per page</span>
                  </div>

                  <InputGroup style={{ width: "250px" }}>
                    <InputGroup.Text><Icon.Search size={14} /></InputGroup.Text>
                    <FormControl
                      size="sm"
                      placeholder="Search doctors..."
                      value={globalFilter ?? ""}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                  </InputGroup>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover table-sm align-middle">
                        <thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <th
                                  key={header.id}
                                  style={{ width: header.getSize() }}
                                  className="text-nowrap"
                                >
                                  {header.isPlaceholder ? null : (
                                    <div
                                      className={header.column.getCanSort() ? "cursor-pointer select-none d-flex align-items-center" : ""}
                                      onClick={header.column.getToggleSortingHandler()}
                                    >
                                      {flexRender(header.column.columnDef.header, header.getContext())}
                                      {header.column.getCanSort() && (
                                        <span className="ms-1">
                                          {{ asc: <Icon.ChevronUp size={12} />, desc: <Icon.ChevronDown size={12} /> }[header.column.getIsSorted()] ?? (
                                            <Icon.ChevronExpand size={12} className="text-muted" />
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                              <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                  <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={columns.length} className="text-center text-muted py-4">
                                No {doctorType === "1" ? "secondary" : "core"} doctors found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <div>
                        Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                        {Math.min(
                          (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                          table.getFilteredRowModel().rows.length
                        )}{" "}
                        of {table.getFilteredRowModel().rows.length} entries
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-outline-secondary" disabled={!table.getCanPreviousPage()} onClick={() => table.setPageIndex(0)}>«</button>
                        <button className="btn btn-outline-secondary" disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>‹</button>
                        <button className="btn btn-outline-secondary" disabled>
                          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                        </button>
                        <button className="btn btn-outline-secondary" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>›</button>
                        <button className="btn btn-outline-secondary" disabled={!table.getCanNextPage()} onClick={() => table.setPageIndex(table.getPageCount() - 1)}>»</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ══════════════ Single Add/Edit Modal ══════════════ */}
            <Modal
              className="modal-size md modal-top-center"
              show={showModal}
              centered
              style={{ display: "flex" }}
              onHide={() => { setShowModal(false); document.activeElement.blur(); }}
              backdrop="static"
              keyboard={false}
            >
              <Modal.Header closeButton>
                <Modal.Title as="h6">
                  {editingId ? "EDIT" : "ADD"} {doctorType === "1" ? "SECONDARY DOCTOR" : "CORE DOCTOR"}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="css">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>

                    <div>
                      <h6>Doctor Name</h6>
                      <input className="form-control" name="doctor_name" placeholder="Enter doctor name" value={form.doctor_name} onChange={handleChange} />
                    </div>

                    <div>
                      <h6>Qualification</h6>
                      <input className="form-control" name="doctor_qualification" placeholder="Enter qualification" value={form.doctor_qualification} onChange={handleChange} />
                    </div>

                    <div>
                      <h6>Mobile</h6>
                      <input className="form-control" name="doctor_mobile" placeholder="Enter mobile number" value={form.doctor_mobile} onChange={handleChange} />
                    </div>

                    <div>
                      <h6>Email</h6>
                      <input className="form-control" name="doctor_email" placeholder="Enter email address" value={form.doctor_email} onChange={handleChange} />
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <h6>Address</h6>
                      <input className="form-control" name="doctor_address" placeholder="Enter address" value={form.doctor_address} onChange={handleChange} />
                    </div>

                    <div className="staff-group">
                      <h6><span style={{ color: "red" }}>*</span>Country</h6>
                      <select className="form-control select" name="doctor_countryname" value={form.doctor_countryname} onChange={(e) => { setForm({ ...form, doctor_countryname: e.target.value }); changeTitle(e.target.value); }}>
                        <option value="" disabled>Select Country</option>
                        {cntry_name.map((country) => (
                          <option key={country.cntry_id} value={country.cntry_id}>{country.cntry_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="staff-group">
                      <h6><span style={{ color: "red" }}>*</span>State</h6>
                      <select className="form-control select" name="doctor_statename" value={form.doctor_statename} onChange={(e) => { setForm({ ...form, doctor_statename: e.target.value }); changeDistricts(e.target.value); }}>
                        <option value="" disabled>Select State</option>
                        {state_name.map((state) => (
                          <option key={state.state_id} value={state.state_id}>{state.state_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="staff-group">
                      <h6><span style={{ color: "red" }}>*</span>District</h6>
                      <select className="form-control select" name="doctor_districtname" value={form.doctor_districtname} onChange={(e) => setForm({ ...form, doctor_districtname: e.target.value })}>
                        <option value="" disabled>Select District</option>
                        {district_name.map((district) => (
                          <option key={district.district_id} value={district.district_id}>{district.district_name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <h6>PIN</h6>
                      <input className="form-control" name="doctor_pin" placeholder="Enter PIN code" value={form.doctor_pin} onChange={handleChange} />
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <h6 className="mb-0">Locations</h6>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openLocationPicker(null)}
                          type="button"
                        >
                          <Icon.GeoAltFill size={14} className="me-1" />
                          Add Location
                        </Button>
                      </div>

                      {form.doctor_locations.length === 0 && (
                        <div className="text-muted small">No locations added yet.</div>
                      )}

                      {form.doctor_locations.map((loc, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="fw-semibold small text-truncate d-flex align-items-center gap-2">
                              {loc.loc_address || "Untitled location"}
                              {loc.loc_default && (
                                <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>
                                  Default
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              {loc.loc_lat}, {loc.loc_lng}
                            </div>
                            <input
                              type="text"
                              className="form-control form-control-sm mt-1"
                              style={{ maxWidth: "160px" }}
                              placeholder="e.g. Home, Clinic"
                              value={loc.loc_type || ""}
                              onChange={(e) => updateLocationType(idx, e.target.value)}
                            />
                          </div>
                          <div className="d-flex gap-2 ms-2 flex-shrink-0 align-items-center">
                            {loc.loc_default ? (
                              <Icon.StarFill
                                className="text-warning"
                                title="Default location"
                                style={{ cursor: "default" }}
                              />
                            ) : (
                              <Icon.Star
                                className="text-secondary"
                                title="Set as default"
                                style={{ cursor: "pointer" }}
                                onClick={() => setDefaultLocation(idx)}
                              />
                            )}
                            <Icon.PencilSquare
                              className="text-primary"
                              style={{ cursor: "pointer" }}
                              onClick={() => openLocationPicker(idx)}
                            />
                            <Icon.Trash
                              className="text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeLocationRow(idx)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <h6>Available Days</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {WEEK_DAYS.map((day) => {
                          const checked = form.doctor_available_days.includes(day);
                          return (
                            <label
                              key={day}
                              className={`btn btn-sm ${checked ? "btn-primary" : "btn-outline-primary"}`}
                              style={{ minWidth: "72px" }}
                            >
                              <input
                                type="checkbox"
                                className="d-none"
                                checked={checked}
                                onChange={() => toggleAvailableDay(day)}
                              />
                              {day}
                            </label>
                          );
                        })}
                      </div>
                      <div className="text-muted small mt-1">
                        Select the days this doctor is available.
                      </div>
                    </div>

                    <div>
                      <h6>Gender</h6>
                      <select className="form-control" name="doctor_gender" value={form.doctor_gender} onChange={handleChange}>
                        <option value="">Select Gender</option>
                        <option value="1">Male</option>
                        <option value="2">Female</option>
                      </select>
                    </div>

                    <div className="staff-group">
                      <h6>Served By (Staff)</h6>
                      <select className="form-control select" name="doctor_servedby" value={form.doctor_servedby} onChange={handleChange}>
                        <option value="">Select Staff</option>
                        {staffList.map((staff) => (
                          <option key={staff.staff_id} value={staff.staff_id}>{staff.staff_name} - {staff.staff_code}</option>
                        ))}
                      </select>
                    </div>

                  </div>
                </form>
              </Modal.Body>

              <Modal.Footer>
                <button className="btn btn-primary" type="button" onClick={handleSubmit}>SAVE</button>
              </Modal.Footer>
            </Modal>

            {/* ══════════════ Map Location Picker Modal ══════════════ */}
            <LocationPickerModal
              show={showLocationPicker}
              onHide={() => { setShowLocationPicker(false); setEditingLocationIndex(null); }}
              onConfirm={handleLocationConfirm}
              initial={editingLocationIndex !== null ? form.doctor_locations[editingLocationIndex] : null}
            />

            <Modal
              show={showBulkModal}
              onHide={closeBulkModal}
              backdrop="static"
              keyboard={false}
              size="lg"
              centered
            >
              <Modal.Header
                closeButton
                className="py-2 px-3"
                style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
              >
                <Modal.Title as="h6" className="mb-0 d-flex align-items-center gap-2 fw-bold text-primary">
                  <Icon.CloudUpload size={16} />
                  Bulk Upload {doctorType === "1" ? "Secondary" : "Core"} Doctors
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="px-3 py-3">
                <div
                  className="mb-3 p-3 rounded"
                  style={{ background: "#f0f4ff", border: "1.5px dashed #aac4ff" }}
                >
                  <label className="form-label small fw-semibold mb-1 text-secondary">
                    <Icon.FileEarmarkExcel size={14} className="me-1 text-success" />
                    Select Excel File (.xlsx / .xls)
                  </label>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    className="form-control form-control-sm"
                    accept=".xlsx,.xls"
                    onChange={handleBulkFile}
                    style={{ background: "#fff" }}
                  />
                </div>

                {bulkErrors.length > 0 && (
                  <div className="alert alert-danger py-2 px-3 mb-3 small rounded">
                    <div className="fw-semibold mb-1">
                      <Icon.ExclamationTriangleFill size={13} className="me-1" />
                      Fix these errors before uploading:
                    </div>
                    <ul className="mb-0 ps-3">
                      {bulkErrors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}

                {bulkRows.length > 0 && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="small fw-semibold text-secondary text-uppercase" style={{ letterSpacing: "0.04em" }}>
                        Preview
                      </span>
                      <span className="small">
                        <span className="badge bg-primary rounded-pill me-1">{bulkRows.length}</span>
                        row(s)
                        {bulkErrors.length === 0 && (
                          <span className="text-success ms-2 fw-semibold">
                            <Icon.CheckCircleFill size={13} className="me-1" />Ready
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="table-responsive rounded" style={{ maxHeight: "240px", overflowY: "auto", border: "1px solid #dee2e6" }}>
                      <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                        <thead style={{ position: "sticky", top: 0, background: "#e9ecef", zIndex: 1 }}>
                          <tr>
                            <th className="px-2 text-secondary">#</th>
                            <th>Name</th><th>Mobile</th><th>Degree</th>
                            <th>Specialization</th><th>Hosp/Clinic</th>
                            <th>Address</th><th>Coordinates</th>
                            <th>Qualification</th><th>Country</th>
                            <th>State</th><th>District</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkRows.map((row, idx) => (
                            <tr key={idx}>
                              <td className="px-2 text-muted">{idx + 1}</td>
                              <td className="fw-semibold">{row.doctor_name}</td>
                              <td>{row.doctor_mobile}</td>
                              <td>{row.doctor_degree || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_specialization || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_hospcli || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_address || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_coordinates || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_qualification || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_countryname || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_statename || <span className="text-muted">—</span>}</td>
                              <td>{row.doctor_districtname || <span className="text-muted">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer className="py-2 px-3" style={{ background: "#f8f9fa", borderTop: "1px solid #dee2e6" }}>
                <Button variant="outline-secondary" size="sm" onClick={closeBulkModal} disabled={bulkUploading}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkSubmit}
                  disabled={bulkRows.length === 0 || bulkErrors.length > 0 || bulkUploading}
                >
                  {bulkUploading ? (
                    <><span className="spinner-border spinner-border-sm me-1" />Uploading...</>
                  ) : (
                    <><Icon.CloudUpload size={14} className="me-1" />Upload{bulkRows.length > 0 ? ` (${bulkRows.length})` : ""}</>
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Doctor;
